import {
  FileNameTypes,
  FileTypes,
  FileUploadStatus,
  useReplaceFilePresignedUrlMutation,
  useDeleteFilesMutation,
  useUpdateFileStatusMutation,
  useUploadFilesMutation,
} from '@/__generated__/graphql';
import { Image as AbyssImage } from '@/components/Analysis/shared/Image';
import * as state from '@/components/Analysis/state';
import { AttachmentMenu } from '@/components/shared/AttachmentDisplay/AttachmentMenu';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { LocationTag } from '@/constants';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { DrawersProps } from '@/types';
import { downloadFile } from '@/utils';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import {
  AssemblyImageAnnotationEditor,
  type AssemblyImageAnnotationEditorHandle,
} from '../ImageAnnotation';
import {
  getThumbnailStyles,
  MainImageContainer,
  NavigationButton,
  SliderContainer,
} from './styles';
import { nextCopyIndexedFileName } from './splitViewPanelCopyNaming';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle, textButtonStyle } from '@/theme/GlobalStyle';

export const SplitViewPanel = ({ allClosed, isOpen, handleOpen }: DrawersProps) => {
  const [selectedIsometricFileId, setSelectedIsometricFileId] = useAtom(
    state.selectedIsometricFileId
  );
  const [assemblyDetails, setAssemblyDetails] = useAtom(state.selectedAssemblyDetails);
  const setSideBySideModeEnabled = useSetAtom(state.sideBySideModeEnabled);
  const [deleteFiles] = useDeleteFilesMutation();
  const [uploadFiles] = useUploadFilesMutation();
  const [replaceFilePresignedUrl] = useReplaceFilePresignedUrlMutation();
  const [updateFileStatus] = useUpdateFileStatusMutation();
  const { rightDrawerWidth } = useDrawerWidths();
  const structureId = useGetStructureId();
  const canAnnotate = useFeatureFlag('work-in-progress');
  const { showSnackBar } = useSnackBarMessage({});

  const referenceDrawingFiles = useMemo(() => {
    return (assemblyDetails?.files ?? [])?.filter(
      (file) =>
        file.name === FileNameTypes.ReferenceDrawing || file.name === FileNameTypes.IsometricDrawing
    );
  }, [assemblyDetails]);

  const selectedReferenceDrawingFile = useMemo(() => {
    return referenceDrawingFiles.find((file) => file.id === selectedIsometricFileId);
  }, [referenceDrawingFiles, selectedIsometricFileId]);

  const handleClose = () => {
    setSelectedIsometricFileId(undefined);
    setSideBySideModeEnabled(false);
  };

  const sliderRef = useRef<Slider>(null);
  const annotationEditorRef = useRef<AssemblyImageAnnotationEditorHandle>(null);
  const [annotationEditorReady, setAnnotationEditorReady] = useState(false);
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [saveAsCopyDialogOpen, setSaveAsCopyDialogOpen] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const handleAnnotationEditorReadyChange = useCallback((ready: boolean) => {
    setAnnotationEditorReady(ready);
  }, []);

  const sliderSettings = useMemo(
    () => ({
      dots: false,
      infinite: true,
      speed: 500,
      slidesToShow: 3,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: '0px',
      arrows: false,
      className: 'center-slide-content',
      variableWidth: false,
      adaptiveHeight: false,
      swipeToSlide: true,
      focusOnSelect: true,
      afterChange: (currentSlide: number) => {
        if (referenceDrawingFiles[currentSlide]) {
          setSelectedIsometricFileId(referenceDrawingFiles[currentSlide]?.id ?? '');
        }
      },
    }),
    [referenceDrawingFiles, setSelectedIsometricFileId]
  );

  const handlePrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const handleNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const handleDownloadAttachment = (attachmentIndex: number) => {
    const currentAttachment = referenceDrawingFiles[attachmentIndex];
    if (!currentAttachment.resourcePath) return;
    downloadFile(getCloudfrontUrl(currentAttachment.resourcePath), currentAttachment.fileName);
  };

  const handleSaveAs = async (blob: Blob) => {
    if (!assemblyDetails?.id || !selectedReferenceDrawingFile) {
      showSnackBar('Cannot save a copy: missing assembly or file.', 'error');
      return;
    }
    if (!structureId) {
      showSnackBar('Cannot save a copy: missing structure.', 'error');
      return;
    }
    if (!selectedReferenceDrawingFile.name) {
      showSnackBar('Cannot save a copy: missing file type.', 'error');
      return;
    }

    try {
      const sourceName = selectedReferenceDrawingFile.fileName ?? 'drawing.png';
      const outExtension = blob.type === 'image/jpeg' ? '.jpg' : '.png';
      const lastDot = sourceName.lastIndexOf('.');
      const nameWithoutExt = lastDot >= 0 ? sourceName.slice(0, lastDot) : sourceName;
      const basisForCopy = `${nameWithoutExt}${outExtension}`;
      const existingNames = (assemblyDetails.files ?? []).map((file) => file.fileName ?? '');
      const fileName = nextCopyIndexedFileName(basisForCopy, existingNames);

      const suffix = `${Date.now()}`;
      const file = new File([blob], fileName, { type: blob.type || 'image/png' });
      const uuid = `copy-${suffix}`;

      const { data } = await uploadFiles({
        variables: {
          input: {
            structureId,
            referenceId: assemblyDetails.id,
            collectionName: 'assembly',
            fileNameType: selectedReferenceDrawingFile.name,
            files: [
              {
                uuid,
                name: file.name,
                mimeType: file.type,
                size: file.size,
              },
            ],
          },
        },
      });

      const presigned = data?.preSignedUrlToUploadFiles?.find((item) => item.uuid === uuid);
      if (!presigned?.url || !presigned.id) {
        throw new Error('Failed to get upload URL');
      }

      const uploadResponse = await fetch(presigned.url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (uploadResponse.status !== 200) {
        throw new Error('Failed to upload annotated image');
      }

      await updateFileStatus({
        variables: {
          input: {
            referenceId: assemblyDetails.id,
            collectionName: 'assembly',
            fileUpdates: [{ fileId: presigned.id, uploadStatus: FileUploadStatus.Success }],
          },
        },
      });

      setAssemblyDetails((previous) => {
        if (!previous) return previous;
        const nextFiles = [
          ...(previous.files ?? []),
          {
            id: presigned.id,
            resourcePath: presigned.resourcePath ?? '',
            fileName,
            name: selectedReferenceDrawingFile.name,
            type:
              fileName.toLowerCase().endsWith('.jpg') || fileName.toLowerCase().endsWith('.jpeg')
                ? (FileTypes.Jpeg as FileTypes)
                : (FileTypes.Png as FileTypes),
            uploadStatus: FileUploadStatus.Success,
            uuid,
            caption: selectedReferenceDrawingFile.caption ?? '',
          },
        ];
        return { ...previous, files: nextFiles };
      });

      showSnackBar(`Saved copy as ${fileName}`, 'success');
    } catch (error) {
      showSnackBar(error instanceof Error ? error.message : 'Failed to save a copy', 'error');
    }
  };

  const handleSave = async (blob: Blob) => {
    if (!assemblyDetails?.id || !selectedReferenceDrawingFile?.id) {
      showSnackBar('Cannot save: missing assembly or file.', 'error');
      return;
    }

    try {
      const { data } = await replaceFilePresignedUrl({
        variables: {
          input: {
            collectionName: 'assembly',
            referenceId: assemblyDetails.id,
            fileId: selectedReferenceDrawingFile.id,
          },
        },
      });

      const url = data?.preSignedUrlToReplaceFile?.url;
      if (!url) throw new Error('Failed to get overwrite URL');

      const uploadResponse = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': blob.type || 'image/png' },
        body: blob,
      });

      if (uploadResponse.status !== 200) {
        throw new Error('Failed to overwrite image');
      }

      const newResourcePath = data?.preSignedUrlToReplaceFile?.resourcePath;

      await updateFileStatus({
        variables: {
          input: {
            referenceId: assemblyDetails.id,
            collectionName: 'assembly',
            fileUpdates: [
              {
                fileId: selectedReferenceDrawingFile.id,
                uploadStatus: FileUploadStatus.Success,
                ...(newResourcePath &&
                  newResourcePath.length > 0 && { resourcePath: newResourcePath }),
              },
            ],
          },
        },
      });

      setAssemblyDetails((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          files: previous.files?.map((file) =>
            file.id === selectedReferenceDrawingFile.id
              ? {
                  ...file,
                  ...(newResourcePath &&
                    newResourcePath.length > 0 && { resourcePath: newResourcePath }),
                  uploadStatus: FileUploadStatus.Success,
                }
              : file
          ),
        };
      });

      showSnackBar('Saved successfully', 'success');
    } catch (error) {
      showSnackBar(error instanceof Error ? error.message : 'Failed to save', 'error');
    }
  };

  const handleRequestOverwriteSave = () => {
    setOverwriteDialogOpen(true);
  };

  const handleConfirmOverwriteSave = async () => {
    if (!annotationEditorRef.current) {
      showSnackBar('Cannot save: editor is not ready.', 'error');
      return;
    }
    setOverwriteDialogOpen(false);
    setSaveInProgress(true);
    try {
      await annotationEditorRef.current.save();
    } catch (error) {
      showSnackBar(error instanceof Error ? error.message : 'Failed to save', 'error');
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleRequestSaveAsCopy = () => {
    setSaveAsCopyDialogOpen(true);
  };

  const handleConfirmSaveAsCopy = async () => {
    if (!annotationEditorRef.current) {
      showSnackBar('Cannot save a copy: editor is not ready.', 'error');
      return;
    }
    setSaveAsCopyDialogOpen(false);
    setSaveInProgress(true);
    try {
      await annotationEditorRef.current.saveAs();
    } catch (error) {
      showSnackBar(error instanceof Error ? error.message : 'Failed to save a copy', 'error');
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleRemoveFile = (fileIndex: number) => {
    const currentFile = referenceDrawingFiles[fileIndex];
    if (!currentFile.id) return;
    deleteFiles({
      variables: {
        input: {
          referenceId: assemblyDetails?.id ?? '',
          collectionName: 'assembly',
          fileIds: [currentFile.id],
        },
      },
    });
    setAssemblyDetails((previous) => {
      if (!previous) return;
      return {
        ...previous,
        files: previous.files?.filter((file) => file.id !== currentFile.id),
      };
    });
    if (referenceDrawingFiles.length === 1) {
      setSelectedIsometricFileId(undefined);
      setSideBySideModeEnabled(false);
    } else {
      let nextFileId = '';

      if (fileIndex === referenceDrawingFiles.length - 1) {
        const prevFile = referenceDrawingFiles[fileIndex - 1];
        nextFileId = prevFile?.id ?? '';
      } else {
        const nextFile = referenceDrawingFiles[fileIndex + 1];
        nextFileId = nextFile?.id ?? '';
      }
      setSelectedIsometricFileId(nextFileId);
    }
  };
  if (!assemblyDetails || !selectedIsometricFileId) return null;

  return (
    <SideDrawer
      allDrawersClosed={allClosed}
      position="right"
      width={rightDrawerWidth}
      isOpen={isOpen}
      handleOpen={handleOpen}
      iconStyles={{ top: 10, zIndex: '102 !important' }}
      sxPaper={{
        overflowY: 'hidden',
        border: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        padding="2.4rem"
        gap="1.6rem"
        id={LocationTag.ViewerSBSIsometricsPanel}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          gap="0.8rem"
        >
          <Typography
            variant="h6"
            fontWeight={700}
            lineHeight="1.6rem"
            sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {selectedReferenceDrawingFile?.caption ?? selectedReferenceDrawingFile?.fileName ?? ''}
          </Typography>
          <Stack direction="row" alignItems="center" gap="0.8rem" flexShrink={0}>
            {canAnnotate && (
              <>
                <Button
                  variant="contained"
                  disabled={!annotationEditorReady || saveInProgress}
                  sx={{ minWidth: '8rem' }}
                  onClick={handleRequestOverwriteSave}
                  data-testid="sbs-save-button"
                >
                  {saveInProgress ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  disabled={!annotationEditorReady || saveInProgress}
                  sx={{ minWidth: '10rem' }}
                  onClick={handleRequestSaveAsCopy}
                  data-testid="sbs-save-as-copy-button"
                >
                  {saveInProgress ? 'Saving…' : 'Save as copy'}
                </Button>
              </>
            )}
            <IconButton
              disableRipple
              sx={{ padding: 0 }}
              onClick={handleClose}
              data-testid="close-button"
              id={`${LocationTag.ViewerSBSIsometricsPanel}-close-button`}
            >
              <Close color="primary" />
            </IconButton>
          </Stack>
        </Stack>
        <GenericDialog
          id="sbs-overwrite-dialog"
          open={overwriteDialogOpen}
          title="Overwrite current file"
          onClose={() => setOverwriteDialogOpen(false)}
          showCloseIcon
          content={
            <Typography fontSize="1.4rem" color={abyssColors.primary[500]}>
              Saving will replace the current file for everyone. If you want to keep the original
              unchanged, use <strong>Save as copy</strong> instead.
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setOverwriteDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleConfirmOverwriteSave()}
                sx={{
                  ...containedButtonStyle,
                  background: abyssColors.error[700],
                  '&:hover': {
                    backgroundColor: abyssColors.error[800],
                  },
                }}
                disabled={saveInProgress}
              >
                {saveInProgress ? 'Saving…' : 'Replace'}
              </Button>
            </>
          }
          actionsStyles={{ mx: 3, pb: 3 }}
        />
        <GenericDialog
          id="sbs-save-as-copy-dialog"
          open={saveAsCopyDialogOpen}
          title="Save a copy"
          onClose={() => setSaveAsCopyDialogOpen(false)}
          showCloseIcon
          content={
            <Typography fontSize="1.4rem" color={abyssColors.primary[500]}>
              A new drawing file will be added to this assembly with an auto-generated name. The
              current file will stay unchanged.
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setSaveAsCopyDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleConfirmSaveAsCopy()}
                sx={containedButtonStyle}
                disabled={saveInProgress}
                data-testid="sbs-save-as-copy-confirm-button"
              >
                {saveInProgress ? 'Saving…' : 'Save copy'}
              </Button>
            </>
          }
          actionsStyles={{ mx: 3, pb: 3 }}
        />
        {selectedReferenceDrawingFile?.resourcePath && (
          <MainImageContainer className={`${LocationTag.ViewerSBSIsometricsPanel}-main-image`}>
            <AssemblyImageAnnotationEditor
              ref={annotationEditorRef}
              imageUrl={getCloudfrontUrl(selectedReferenceDrawingFile.resourcePath)}
              onSave={handleSave}
              onSaveAs={handleSaveAs}
              onReadyChange={handleAnnotationEditorReadyChange}
              canAnnotate={canAnnotate}
            />
          </MainImageContainer>
        )}
        <SliderContainer>
          <Box sx={{ width: '60%', margin: '0 auto', position: 'relative', overflow: 'visible' }}>
            <NavigationButton
              onClick={handlePrev}
              className="prev-button"
              data-testid="prev-button"
              disableRipple
            >
              <ChevronLeft sx={{ fontSize: '2.4rem' }} />
            </NavigationButton>

            <Slider ref={sliderRef} {...sliderSettings}>
              {referenceDrawingFiles.map((file, fileIndex) => (
                <Stack key={file.id}>
                  <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    gap="0.4rem"
                    width="100%"
                    data-testid={`thumbnail-container-${file.id}`}
                    position="relative"
                    className={`${LocationTag.ViewerSBSIsometricsPanel}-thumbnail`}
                    sx={{ margin: '0 auto', maxWidth: '100%', overflow: 'hidden' }}
                  >
                    <AbyssImage
                      src={getCloudfrontUrl(file?.resourcePath)}
                      alt={file?.name}
                      imgSx={{
                        height: '7.8rem',
                        width: '14.2rem',
                        margin: '0 auto',
                        display: 'block',
                        objectFit: 'cover',
                      }}
                      sx={{
                        ...getThumbnailStyles(selectedIsometricFileId, file),
                        display: 'flex',
                        justifyContent: 'center',
                        margin: '0 auto',
                      }}
                      onClick={() => setSelectedIsometricFileId(file?.id ?? '')}
                      data-testid={`thumbnail-${file.id}`}
                    />
                    <Tooltip arrow title={file?.caption ?? file?.fileName ?? ''} placement="bottom">
                      <Typography
                        variant="caption"
                        color="primary.400"
                        fontWeight={500}
                        maxWidth="9rem"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        display="block"
                        textAlign="center"
                      >
                        {file?.caption ?? file?.fileName ?? ''}
                      </Typography>
                    </Tooltip>
                    <AttachmentMenu
                      canViewSideBySide={false}
                      handleSBSMode={() => {}}
                      handleRemoveAttachment={handleRemoveFile}
                      handleDownloadAttachment={handleDownloadAttachment}
                      index={fileIndex}
                      location={LocationTag.ViewerSBSIsometricsPanel}
                    />
                  </Stack>
                </Stack>
              ))}
            </Slider>

            <NavigationButton
              onClick={handleNext}
              className="next-button"
              data-testid="next-button"
              disableRipple
            >
              <ChevronRight sx={{ fontSize: '2.4rem' }} />
            </NavigationButton>
          </Box>
        </SliderContainer>
      </Stack>
    </SideDrawer>
  );
};
